/**
 * A1-style cell references.
 *
 * Columns are base-26 *bijective* — A..Z, then AA..AZ, BA.. — which is not
 * ordinary base 26 because there is no zero digit. Getting this subtly wrong
 * is the classic spreadsheet bug: it works until column Z and then puts AA in
 * the wrong place, so both directions are tested against the boundaries.
 *
 * Coordinates are zero-based everywhere inside the engine. Only the labels the
 * user sees are one-based.
 */
/** Excel's own ceiling, and a sane guard against a typo like `A1:A99999999`. */
export const MAX_COLUMNS = 16_384;
export const MAX_ROWS = 1_048_576;
const A = 'A'.charCodeAt(0);
/** 0 -> "A", 25 -> "Z", 26 -> "AA". */
export function columnLabel(col) {
    let n = col;
    let label = '';
    while (n >= 0) {
        label = String.fromCharCode(A + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    }
    return label;
}
/** "A" -> 0, "Z" -> 25, "AA" -> 26. Returns null for anything else. */
export function columnIndex(label) {
    if (!label)
        return null;
    let n = 0;
    for (const character of label.toUpperCase()) {
        const digit = character.charCodeAt(0) - A;
        if (digit < 0 || digit > 25)
            return null;
        n = n * 26 + digit + 1;
    }
    return n - 1;
}
export function formatRef(ref) {
    const col = `${ref.colAbsolute ? '$' : ''}${columnLabel(ref.col)}`;
    const row = `${ref.rowAbsolute ? '$' : ''}${ref.row + 1}`;
    return `${col}${row}`;
}
export function formatRange(range) {
    return `${formatRef(range.start)}:${formatRef(range.end)}`;
}
const REF_PATTERN = /^(\$?)([A-Za-z]{1,3})(\$?)(\d{1,7})$/;
export function parseRef(text) {
    const match = REF_PATTERN.exec(text.trim());
    if (!match)
        return null;
    const [, colDollar = '', letters = '', rowDollar = '', digits = ''] = match;
    const col = columnIndex(letters);
    const row = Number(digits) - 1;
    if (col === null || col >= MAX_COLUMNS)
        return null;
    if (!Number.isInteger(row) || row < 0 || row >= MAX_ROWS)
        return null;
    return {
        col,
        row,
        ...(colDollar ? { colAbsolute: true } : {}),
        ...(rowDollar ? { rowAbsolute: true } : {}),
    };
}
/** A key for maps and dependency sets. Absoluteness is deliberately dropped —
 *  `$A$1` and `A1` are the same cell, and only the reference text differs. */
export function cellKey(col, row) {
    return `${columnLabel(col)}${row + 1}`;
}
export function parseKey(key) {
    const ref = parseRef(key);
    return ref ? { col: ref.col, row: ref.row } : null;
}
/** Corner-order independent: `B5:A1` covers the same cells as `A1:B5`. */
export function normaliseRange(range) {
    return {
        top: Math.min(range.start.row, range.end.row),
        bottom: Math.max(range.start.row, range.end.row),
        left: Math.min(range.start.col, range.end.col),
        right: Math.max(range.start.col, range.end.col),
    };
}
/** Every cell key in a range, row-major — the order SUM and friends see. */
export function* rangeKeys(range) {
    const { top, left, bottom, right } = normaliseRange(range);
    for (let row = top; row <= bottom; row += 1) {
        for (let col = left; col <= right; col += 1) {
            yield cellKey(col, row);
        }
    }
}
export function rangeSize(range) {
    const { top, left, bottom, right } = normaliseRange(range);
    return (bottom - top + 1) * (right - left + 1);
}
/** A reference that pointed at deleted cells. Rendered as `#REF!`. */
export const REF_DELETED = Symbol('ref-deleted');
/**
 * Move a reference in response to an insert or delete.
 *
 * Three cases, and the third is the one people forget: a reference *into* the
 * deleted band has no cell left to point at and must become #REF!, rather than
 * silently sliding onto whatever row took its place. Sliding is worse than an
 * error — it produces a plausible wrong number.
 *
 * Absolute (`$A$1`) does not exempt a reference from this. `$` fixes a
 * reference when the *formula* is copied; it does not stop the cell itself
 * from being moved by a structural edit.
 */
export function shiftRef(ref, spec) {
    const position = spec.axis === 'row' ? ref.row : ref.col;
    if (spec.delta < 0) {
        const removedTo = spec.at - spec.delta; // exclusive
        if (position >= spec.at && position < removedTo)
            return REF_DELETED;
    }
    if (position < spec.at)
        return ref;
    const moved = position + spec.delta;
    return spec.axis === 'row' ? { ...ref, row: moved } : { ...ref, col: moved };
}
/**
 * Shift a range. A range only dies when the edit removes all of it; if part
 * survives, the range contracts. That is what makes `=SUM(A1:A10)` keep
 * working — and keep being correct — after a row inside it is deleted.
 */
export function shiftRange(range, spec) {
    const startPos = spec.axis === 'row' ? range.start.row : range.start.col;
    const endPos = spec.axis === 'row' ? range.end.row : range.end.col;
    const low = Math.min(startPos, endPos);
    const high = Math.max(startPos, endPos);
    if (spec.delta < 0) {
        const removedTo = spec.at - spec.delta;
        if (low >= spec.at && high < removedTo)
            return REF_DELETED;
    }
    const move = (ref) => {
        const position = spec.axis === 'row' ? ref.row : ref.col;
        if (position < spec.at)
            return ref;
        let moved;
        if (spec.delta < 0) {
            const removedTo = spec.at - spec.delta;
            // An endpoint inside the deleted band collapses to the edit point rather
            // than becoming #REF!, which is how the range contracts instead of dying.
            moved = position < removedTo ? spec.at : position + spec.delta;
        }
        else {
            moved = position + spec.delta;
        }
        return spec.axis === 'row' ? { ...ref, row: moved } : { ...ref, col: moved };
    };
    return { start: move(range.start), end: move(range.end) };
}
/**
 * Offset a reference by a relative distance — what happens when a formula is
 * copied from one cell to another. `$` anchors are respected here, which is
 * the entire reason they exist.
 */
export function offsetRef(ref, colDelta, rowDelta) {
    return {
        ...ref,
        col: ref.colAbsolute ? ref.col : ref.col + colDelta,
        row: ref.rowAbsolute ? ref.row : ref.row + rowDelta,
    };
}
/**
 * `A1:B5`, or a bare `A1` meaning the one cell.
 *
 * Sheet qualifiers and dollars are tolerated and dropped: what arrives from a
 * workbook's Print_Area is `'ASFAND SALARY'!$A$1:$H$92`, and the caller wants
 * the rectangle rather than the notation.
 */
export function parseRange(text) {
    let body = text.trim();
    const bang = body.lastIndexOf('!');
    if (bang !== -1)
        body = body.slice(bang + 1);
    const [first = '', second, ...rest] = body.split(':');
    if (rest.length > 0)
        return null;
    const start = parseRef(first);
    if (!start)
        return null;
    if (second === undefined)
        return { start, end: start };
    const end = parseRef(second);
    return end ? { start, end } : null;
}
/**
 * A whole-row or whole-column band — `1:3`, `A:C` — as Print Titles are written.
 *
 * Returns the inclusive index pair, or null. The two forms are told apart by
 * whether the text is digits or letters, which is unambiguous.
 */
export function parseBand(text) {
    const body = text.trim().replace(/\$/g, '');
    const bang = body.lastIndexOf('!');
    const range = bang === -1 ? body : body.slice(bang + 1);
    const [first = '', second = '', ...rest] = range.split(':');
    if (rest.length > 0 || !first || !second)
        return null;
    if (/^\d+$/.test(first) && /^\d+$/.test(second)) {
        const from = Number(first) - 1;
        const to = Number(second) - 1;
        if (from < 0 || to < 0 || from >= MAX_ROWS || to >= MAX_ROWS)
            return null;
        return { from: Math.min(from, to), to: Math.max(from, to), axis: 'row' };
    }
    const from = columnIndex(first);
    const to = columnIndex(second);
    if (from === null || to === null || from >= MAX_COLUMNS || to >= MAX_COLUMNS)
        return null;
    return { from: Math.min(from, to), to: Math.max(from, to), axis: 'column' };
}
//# sourceMappingURL=refs.js.map